import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Add from '@mui/icons-material/Add'
import Build from '@mui/icons-material/Build'
import { useAuth } from 'hooks/auth/useAuth'
import { assistantToolLabel } from './AssistantToolStatus'
import {
  createAssistantTool,
  fetchAssistantTools,
  saveAssistantTool,
  type AssistantToolRecord,
  type AssistantToolSchema,
} from './assistantToolClient'

const EMPTY_SCHEMA = `{
  "type": "object",
  "properties": {},
  "required": []
}`

function schemaText(schema: AssistantToolSchema): string {
  return JSON.stringify(schema, null, 2)
}

function parseSchema(text: string): AssistantToolSchema | null {
  try {
    const value = JSON.parse(text) as AssistantToolSchema
    if (!value || value.type !== 'object' || !value.properties || !Array.isArray(value.required)) {
      return null
    }
    return value
  } catch {
    return null
  }
}

function editedCaption(tool: AssistantToolRecord): string {
  if (!tool.lastEditedBy || !tool.lastEditedAt) return 'Not edited yet'
  const when = new Date(tool.lastEditedAt)
  const stamp = Number.isNaN(when.getTime())
    ? tool.lastEditedAt
    : when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  return `Last edited by ${tool.lastEditedBy.name} on ${stamp}`
}

export function AssistantToolEditorButton() {
  const { userInfo } = useAuth()
  const [open, setOpen] = useState(false)
  if (userInfo?.isOwner !== true) return null
  return (
    <>
      <Tooltip title='Edit tools'>
        <IconButton
          size='small'
          aria-label='Edit assistant tools'
          data-assistant-demo='tools'
          onClick={() => setOpen(true)}
          sx={{ width: 44, height: 44 }}
        >
          <Build fontSize='small' />
        </IconButton>
      </Tooltip>
      <AssistantToolEditorDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function AssistantToolEditorDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tools, setTools] = useState<AssistantToolRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const creatingRef = useRef(false)
  creatingRef.current = creating
  const [intent, setIntent] = useState('')
  const [description, setDescription] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [schema, setSchema] = useState(EMPTY_SCHEMA)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetchAssistantTools()
      .then(rows => {
        if (cancelled) return
        const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name))
        setTools(sorted)
        if (!creatingRef.current) {
          setSelected(current => current ?? sorted[0]?.name ?? null)
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load tools')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const current = creating ? null : (tools.find(tool => tool.name === selected) ?? null)

  useEffect(() => {
    if (!current) return
    setDescription(current.description)
    setEnabled(current.enabled)
    setSchema(schemaText(current.schema))
  }, [current])

  const startNew = () => {
    setCreating(true)
    setSelected(null)
    setIntent('')
    setDescription('')
    setEnabled(true)
    setSchema(EMPTY_SCHEMA)
    setError(null)
  }

  const save = async () => {
    const notes = (creating ? intent : description).trim()
    if (notes.length < 8) {
      setError('Describe the tool in at least 8 characters')
      return
    }
    const parsed = parseSchema(schema)
    setSaving(true)
    setError(null)
    try {
      const saved = creating
        ? await createAssistantTool({ intent: notes, enabled })
        : await saveAssistantTool(current?.name || '', {
            description: notes,
            enabled,
            ...(parsed ? { schema: parsed } : {}),
          })
      setTools(prev => {
        const next = prev.some(tool => tool.name === saved.name)
          ? prev.map(tool => (tool.name === saved.name ? saved : tool))
          : [...prev, saved]
        return next.sort((a, b) => a.name.localeCompare(b.name))
      })
      setCreating(false)
      setSelected(saved.name)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save this tool')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          height: { xs: '100%', md: 'min(860px, 92vh)' },
          m: { xs: 0, md: 2 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>Portal assistant tools</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5, flexShrink: 0 }}>
          The list scrolls. The description stays here. Save always drafts and formats the tool with AI, and stores it only when it is a proper tool. The next reply can call it.
        </Typography>
        {loading ? (
          <Typography variant='body2'>Loading tools…</Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', md: 260 },
                maxHeight: { xs: 220, md: 'none' },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderRight: { md: '1px solid' },
                borderColor: 'divider',
                pr: { md: 1 },
              }}
            >
              <Button
                startIcon={<Add />}
                onClick={startNew}
                sx={{ alignSelf: 'flex-start', mb: 1, flexShrink: 0 }}
              >
                New tool
              </Button>
              <List dense sx={{ overflow: 'auto', flex: 1 }}>
                {tools.map(tool => (
                  <ListItemButton
                    key={tool.name}
                    selected={!creating && tool.name === selected}
                    onClick={() => {
                      setCreating(false)
                      setSelected(tool.name)
                      setError(null)
                    }}
                  >
                    <ListItemText
                      primary={assistantToolLabel(tool.name)}
                      secondary={tool.enabled ? tool.name : 'Off'}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {creating ? (
                <Box sx={{ flexShrink: 0 }}>
                  <TextField
                    label='What should this tool do?'
                    value={intent}
                    onChange={event => setIntent(event.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder='Example: compare repair spend by shop for the trucks we sold this year'
                  />
                  <FormControlLabel
                    control={<Switch checked={enabled} onChange={(_, checked) => setEnabled(checked)} />}
                    label='Enabled'
                    sx={{ mt: 1 }}
                  />
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Save drafts the name, description, and schema with AI. It is stored only when that result is a proper tool.
                  </Typography>
                </Box>
              ) : current ? (
                <>
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                      {editedCaption(current)}
                    </Typography>
                    <FormControlLabel
                      control={<Switch checked={enabled} onChange={(_, checked) => setEnabled(checked)} />}
                      label='Enabled'
                    />
                    <TextField
                      label='Description'
                      value={description}
                      onChange={event => setDescription(event.target.value)}
                      fullWidth
                      multiline
                      minRows={4}
                      sx={{ mt: 1, mb: 1.5 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <TextField
                      label='Schema'
                      value={schema}
                      onChange={event => setSchema(event.target.value)}
                      fullWidth
                      multiline
                      minRows={8}
                    />
                  </Box>
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Select a tool, or choose New tool.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {error ? (
          <Typography variant='body2' color='error' sx={{ mt: 1, flexShrink: 0 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant='contained'
          onClick={() => void save()}
          disabled={(!creating && !current) || saving || loading}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
